/* Copyright (c) 2015 Google Inc. All rights reserved. */
/**
The `google-map` element renders a Google Map.

<b>Example</b>:

    <style>
      google-map {
        height: 600px;
      }
    </style>
    <google-map latitude="37.77493" longitude="-122.41942" api-key="1234"></google-map>

<b>Example</b> - add markers to the map and ensure they're in view:

    <google-map latitude="37.77493" longitude="-122.41942" fit-to-markers>
      <google-map-marker latitude="37.779" longitude="-122.3892"
          draggable="true" title="Go Giants!"></google-map-marker>
      <google-map-marker latitude="37.777" longitude="-122.38911"></google-map-marker>
    </google-map>

<b>Example</b>:

    <google-map disable-default-ui zoom="15"></google-map>
    <script>
      var map = document.querySelector('google-map');
      map.latitude = 37.77493;
      map.longitude = -122.41942;
      map.addEventListener('google-map-ready', function(e) {
        alert('Map loaded!');
      });
    </script>

<b>Example</b> - with Google directions, using data-binding inside another Polymer element

    <google-map map="{{map}}"></google-map>
    <google-map-directions map="[[map]]"
        start-address="San Francisco" end-address="Mountain View">
    </google-map-directions>

Disable dragging by adding `draggable="false"` on the `google-map` element.

<b>Example</b> - loading the Maps API from another origin (China)

    <google-map maps-url="http://maps.google.cn/maps/api/js?callback=%%callback%%">

###  Tips

If you're seeing the message "You have included the Google Maps API multiple times on this page. This may cause unexpected errors." it probably means you're loading other maps elements on the page (`<google-maps-directions>`). Each maps element must include the same set of configuration options (`apiKey`, `clientId`, `language`, `version`, etc.) so the Maps API is loaded from the same URL.

@demo demo/index.html
@demo demo/polys.html
@demo demo/kml.html
*/
/*
  FIXME(polymer-modulizer): the above comments were extracted
  from HTML and may be out of place here. Review them and
  then delete this comment!
*/
import '@polymer/polymer/polymer-legacy.js';

import { IronResizableBehavior } from '@polymer/iron-resizable-behavior/iron-resizable-behavior.js';
import '@polymer/iron-selector/iron-selector.js';
import '../google-apis/google-maps-api.js';
import './google-map-marker.js';
import { Polymer } from '@polymer/polymer/lib/legacy/polymer-fn.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';
import { dom } from '@polymer/polymer/lib/legacy/polymer.dom.js';
import { KatapultGeometry } from 'katapult-toolbox';
import { logger } from '@sentry/browser';

Polymer({
  _template: html`
    <style>
      :host {
        position: relative;
        display: block;
        height: 100%;
      }

      #map {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        @apply (--google-map-style);
      }
      .gmnoscreen,
      .gm-style-cc,
      img[src='https://maps.gstatic.com/mapfiles/api-3/images/google_white5_hdpi.png'] {
        display: none;
      }
      .gm-style > .gmnoprint:not(.gm-bundled-control) {
        z-index: 5 !important;
        pointer-events: none !important;
      }
      .gm-svpc {
        border-radius: 20px !important;
      }
      .gmnoprint button {
        border-radius: 20px !important;
      }

      /* FIX TO TRIPLE ICON SHOWING IN BUTTONS - 2019/03/28 */
      .gm-control-active > img {
        box-sizing: content-box;
        display: none;
        left: 50%;
        pointer-events: none;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .gm-control-active > img:nth-child(1) {
        display: block;
      }

      .gm-control-active:hover > img:nth-child(1),
      .gm-control-active:active > img:nth-child(1) {
        display: none;
      }

      .gm-control-active:hover > img:nth-child(2),
      .gm-control-active:active > img:nth-child(3) {
        display: block;
      }
      /*----------------------------------------------------------*/

      div.rich-marker {
        white-space: nowrap;
        background-color: rgba(255, 255, 255, 0.8);
        border-radius: 24px;
        @apply --shadow-elevation-2dp;
      }
      /* div.rich-marker::before {
      content:'';
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid rgba(255, 255, 255, 0.6);
      position:absolute;
      left:50%;
      top:-10px;
      margin-left: -10px;
      z-index: 4;
    } */
      div.rich-marker katapult-button[iconOnly] {
        padding: 5px;
        width: 35px;
        height: 35px;
        transition: color 0.1s;
      }
      div.rich-marker katapult-button[iconOnly].delete:hover {
        color: var(--paper-red-500);
      }
      div.rich-marker katapult-button[iconOnly].edit:hover {
        color: var(--secondary-color);
      }
      katapult-button[loading] {
        pointer-events: auto !important;
        --katapult-button-paper-spinner: {
          padding: 0;
          width: 40px;
          height: 40px;
          margin: -8px;
        };
        --katapult-button[loading]: {
          opacity: 1;
        };
      }

      /* Until Advanced Map Markers provide ways for disabling the focus outline and dragging cross, these are workarounds */
      #map div.yNHHyP-marker-view:focus-visible {
        outline: none !important;
      }
      img[src='https://maps.gstatic.com/mapfiles/api-3/images/drag-cross.png'] {
        display: none;
      }
    </style>

    <google-maps-api
      id="api"
      api-key="[[apiKey]]"
      channel="[[apiChannel]]"
      client-id="[[clientId]]"
      version="[[version]]"
      signed-in="[[signedIn]]"
      language="[[language]]"
      on-api-load="_mapApiLoaded"
      on-library-error-message-changed="_mapApiError"
      maps-url="[[mapsUrl]]"
    >
    </google-maps-api>

    <div id="map" class="ph-no-capture"></div>
    <iron-selector
      id="selector"
      multi="[[!singleInfoWindow]]"
      selected-attribute="open"
      activate-event="google-map-marker-open"
      on-google-map-marker-close="_deselectMarker"
    >
      <slot id="markers" name="markers"></slot>
    </iron-selector>
    <slot id="objects" name="objects"></slot>
  `,

  is: 'google-map',

  /**
   * Fired when the Maps API has fully loaded.
   * @event google-map-ready
   */
  /**
   * Fired when the user clicks on the map (but not when they click on a marker, infowindow, or
   * other object). Requires the clickEvents attribute to be true.
   * @event google-map-click
   * @param {google.maps.MouseEvent} event The mouse event.
   */
  /**
   * Fired when the user double-clicks on the map. Note that the google-map-click event will also fire,
   * right before this one. Requires the clickEvents attribute to be true.
   * @event google-map-dblclick
   * @param {google.maps.MouseEvent} event The mouse event.
   */
  /**
   * Fired repeatedly while the user drags the map. Requires the dragEvents attribute to be true.
   * @event google-map-drag
   */
  /**
   * Fired when the user stops dragging the map. Requires the dragEvents attribute to be true.
   * @event google-map-dragend
   */
  /**
   * Fired when the user starts dragging the map. Requires the dragEvents attribute to be true.
   * @event google-map-dragstart
   */
  /**
   * Fired whenever the user's mouse moves over the map container. Requires the mouseEvents attribute to
   * be true.
   * @event google-map-mousemove
   * @param {google.maps.MouseEvent} event The mouse event.
   */
  /**
   * Fired when the user's mouse exits the map container. Requires the mouseEvents attribute to be true.
   * @event google-map-mouseout
   * @param {google.maps.MouseEvent} event The mouse event.
   */
  /**
   * Fired when the user's mouse enters the map container. Requires the mouseEvents attribute to be true.
   * @event google-map-mouseover
   * @param {google.maps.MouseEvent} event The mouse event.
   */
  /**
   * Fired when the DOM `contextmenu` event is fired on the map container. Requires the clickEvents
   * attribute to be true.
   * @event google-map-rightclick
   * @param {google.maps.MouseEvent} event The mouse event.
   */
  /**
   * Fired when the map becomes idle after panning or zooming.
   * @event google-map-idle
   */

  /**
   * Polymer properties for the google-map custom element.
   */
  properties: {
    /**
     * A Maps API key. To obtain an API key, see https://developers.google.com/maps/documentation/javascript/tutorial#api_key.
     */
    apiKey: String,

    apiChannel: String,

    /**
     * Overrides the origin the Maps API is loaded from. Defaults to `https://maps.googleapis.com`.
     */
    mapsUrl: {
      type: String
      // Initial value set in google-maps-api.
    },

    /**
     * A Maps API for Business Client ID. To obtain a Maps API for Business Client ID, see https://developers.google.com/maps/documentation/business/.
     * If set, a Client ID will take precedence over an API Key.
     */
    clientId: String,

    /**
     * A latitude to center the map on.
     */
    latitude: {
      type: Number,
      value: 37.77493,
      notify: true,
      reflectToAttribute: true
    },

    /**
     * A Maps API object.
     */
    map: {
      type: Object,
      notify: true,
      value: null
    },

    /**
     * A longitude to center the map on.
     */
    longitude: {
      type: Number,
      value: -122.41942,
      notify: true,
      reflectToAttribute: true
    },

    /**
     * A kml file to load.
     */
    kml: {
      type: String,
      value: null,
      observer: '_loadKml'
    },

    /**
     * A zoom level to set the map to.
     */
    zoom: {
      type: Number,
      value: 10,
      observer: '_zoomChanged',
      notify: true
    },

    /**
     * When set, prevents the map from tilting (when the zoom level and viewport supports it).
     */
    noAutoTilt: {
      type: Boolean,
      value: false
    },

    /**
     * Map type to display. One of 'roadmap', 'satellite', 'hybrid', 'terrain'.
     */
    mapType: {
      type: String,
      value: 'roadmap', // roadmap, satellite, hybrid, terrain,
      notify: true
    },

    /**
     * Object containing the available map types ids as the keys and their titles as the values
     */
    mapTypeIds: {
      type: Object,
      value: {
        hybrid: 'Hybrid',
        roadmap: 'Roadmap',
        satellite: 'Satellite',
        terrain: 'Terrain',
        blank: 'Blank'
      },
      notify: true
    },

    /**
     * Version of the Google Maps API to use.
     */
    version: {
      type: String,
      value: '3.65'
    },

    /**
     * If set, removes the map's default UI controls.
     */
    disableDefaultUi: {
      type: Boolean,
      value: false,
      observer: '_disableDefaultUiChanged'
    },

    /**
     * If set, removes the map's 'map type' UI controls.
     */
    disableMapTypeControl: {
      type: Boolean,
      value: false,
      observer: '_disableMapTypeControlChanged'
    },

    /**
     * If set, removes the map's 'street view' UI controls.
     */
    disableStreetViewControl: {
      type: Boolean,
      value: false,
      observer: '_disableStreetViewControlChanged'
    },

    /**
     * If set, the zoom level is set such that all markers (google-map-marker children) are brought into view.
     */
    fitToMarkers: {
      type: Boolean,
      value: false,
      observer: '_fitToMarkersChanged'
    },

    /**
     * If true, prevent the user from zooming the map interactively.
     */
    disableZoom: {
      type: Boolean,
      value: false,
      observer: '_disableZoomChanged'
    },

    /**
     * If set, custom styles can be applied to the map.
     * For style documentation see https://developers.google.com/maps/documentation/javascript/reference#MapTypeStyle
     */
    styles: {
      type: Object,
      value: function () {
        return {};
      }
    },

    /**
     * A maximum zoom level which will be displayed on the map.
     */
    maxZoom: {
      type: Number,
      observer: '_maxZoomChanged'
    },

    /**
     * A minimum zoom level which will be displayed on the map.
     */
    minZoom: {
      type: Number,
      observer: '_minZoomChanged'
    },

    /**
     * If true, sign-in is enabled.
     * See https://developers.google.com/maps/documentation/javascript/signedin#enable_sign_in
     */
    signedIn: {
      type: Boolean,
      value: false
    },

    /**
     * The localized language to load the Maps API with. For more information
     * see https://developers.google.com/maps/documentation/javascript/basics#Language
     *
     * Note: the Maps API defaults to the preffered language setting of the browser.
     * Use this parameter to override that behavior.
     */
    language: {
      type: String
    },

    /**
     * When true, map *click events are automatically registered.
     */
    clickEvents: {
      type: Boolean,
      value: false,
      observer: '_clickEventsChanged'
    },

    /**
     * When true, map drag* events are automatically registered.
     */
    dragEvents: {
      type: Boolean,
      value: false,
      observer: '_dragEventsChanged'
    },

    /**
     * When true, file drag* events are automatically registered.
     */
    dragNDropEvents: {
      type: Boolean,
      value: false,
      observer: '_dragNDropEventsChanged'
    },

    noKeyboardShortcuts: {
      type: Boolean,
      value: false
    },

    /**
     * When true, map mouse* events are automatically registered.
     */
    mouseEvents: {
      type: Boolean,
      value: false,
      observer: '_mouseEventsChanged'
    },

    /**
     * Additional map options for google.maps.Map constructor.
     * Use to specify additional options we do not expose as
     * properties.
     * Ex: `<google-map additional-map-options='{"mapTypeId":"satellite"}'>`
     *
     * Note, you can't use API enums like `google.maps.ControlPosition.TOP_RIGHT`
     * when using this property as an HTML attribute. Instead, use the actual
     * value (e.g. `3`) or set `.additionalMapOptions` in JS rather than using
     * the attribute.
     */
    additionalMapOptions: {
      type: Object,
      value: function () {
        return {};
      }
    },

    /**
     * The markers on the map.
     */
    markers: {
      type: Array,
      value: function () {
        return [];
      },
      readOnly: true
    },

    /**
     * The non-marker objects on the map.
     */
    objects: {
      type: Array,
      value: function () {
        return [];
      },
      readOnly: true
    },

    /**
     * If set, all other info windows on markers are closed when opening a new one.
     */
    singleInfoWindow: {
      type: Boolean,
      value: false
    },

    // We added snapLine, snapLength, and snapAngle to allow snapping map in /mobile
    snapLine: Object,
    snapLength: Object,
    snapAngle: Object,
    measurable: {
      type: Boolean,
      value: false,
      observer: '_showButton'
    },
    googleMapReady: {
      type: Boolean,
      value: false,
      notify: true
    }
  },

  behaviors: [IronResizableBehavior],

  listeners: {
    'iron-resize': 'resize'
  },

  observers: [
    '_debounceUpdateCenter(latitude, longitude)',
    '_mapTypeChanged(mapType, mapTypeIds.*)',
    '_additionalOptionsChanged(additionalMapOptions.*)',
    'updateSnappedPosition(snapLine, snapLength, snapAngle)'
  ],

  attached: function () {
    this._initGMap();
  },

  detached: function () {
    if (this._markersChildrenListener) {
      this.unlisten(this.$.selector, 'items-changed', '_updateMarkers');
      this._markersChildrenListener = null;
    }
    if (this._objectsMutationObserver) {
      this._objectsMutationObserver.disconnect();
      this._objectsMutationObserver = null;
    }

    document.removeEventListener('keydown', this.keydownHandler);
  },

  _initGMap: function () {
    if (this.map) {
      return; // already initialized
    }
    if (this.$.api.apiLoaded !== true) {
      return; // api not loaded
    }
    if (!this.isAttached) {
      return; // not attached
    }

    this.map = new google.maps.Map(this.$.map, this._getMapOptions());

    this._listeners = {};
    this._updateCenter();
    this._loadKml();
    this._updateMarkers();
    this._updateObjects();
    this._initBlankMapType();
    this._initMeasureButton();
    this._initDropOverlay();
    this._addMapListeners();
    this._addKeyBoardListeners();
    // Throw the google map ready function as soon as the map is initialized
    this.checkForGoogleMapsReady();
    this.fire('google-map-initialized');
  },

  checkForGoogleMapsReady: function () {
    let clientRect = this.$.map.getBoundingClientRect();
    if (clientRect.height && clientRect.width) {
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        this.googleMapReady = true;
        this.fire('google-map-ready');
      });
    } else {
      setTimeout(() => {
        this.checkForGoogleMapsReady();
      }, 100);
      return;
    }
  },

  _initBlankMapType: function () {
    this.map.mapTypes.set('blank', {
      name: 'Blank',
      alt: 'Blank Map for Over-Zooming',
      maxZoom: 30,
      minZoom: 5,
      tileSize: { width: 256, height: 256 },
      getTile: this.getBlankTile.bind(this)
    });
    //this._initOverlays();
  },

  getBlankTile: function (coord, z, doc) {
    let div = doc.createElement('div'),
      text = doc.createElement('div'),
      lat = this.latitude,
      t = (131479717 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, z);

    div.style.width = div.style.height = '256px';
    div.style.display = 'flex';
    div.style.justifyContent = 'center';
    div.style.fontSize = '10px';
    div.style.color = '#ff8400';
    div.style.backgroundColor = '#003e51';
    div.style.border = '1px solid #007299';

    if (t <= 100) text.textContent = Math.round(100 * t) / 100 + ' ft\n(' + Math.round(100 * (0.3048 * t)) / 100 + ' m)';
    else if (t <= 2640) text.textContent = Math.round(t) + ' ft\n(' + Math.round(0.3048 * t) + ' m)';
    else text.textContent = Math.round(100 * (t / 5280)) / 100 + ' mi\n(' + Math.round(100 * (0.0003048 * t)) / 100 + ' km)';
    text.style.whiteSpace = 'pre';
    text.style.textAlign = text.style.alignSelf = 'center';

    div.appendChild(text);

    return div;
  },

  _initMeasureButton: function () {
    // Build the custom map type select element and add it to controls
    this.measureButton = document.createElement('katapult-button');
    this.measureButton.textContent = 'Measure';
    this.measureButton.icon = 'straighten';
    this.measureButton.style.backgroundColor = 'white';
    this.measureButton.style.width = '40px';
    this.measureButton.style.height = '40px';
    this.measureButton.style.borderRadius = '24px';
    this.measureButton.style.padding = '8px';
    this.measureButton.style.boxSizing = 'border-box';
    this.measureButton.style.marginLeft = '10px';
    this.measureButton.style.marginBottom = '10px';
    this.measureButton.style.boxShadow = 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px';
    this.measureButton.style.color = '#333333';
    this.measureButton.setAttribute('iconOnly', '');
    this.measureButton.setAttribute('noBorder', '');
    this.map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(this.measureButton);

    if (!this.measurable) this.measureButton.style.display = 'none';

    // add `change` event listener
    this.measureButton.addEventListener('click', (event) => {
      if (this.measureButton.textContent === 'Measure') {
        this.fire('measure-map', { start: true, event });
        this.measureButton.textContent = 'Stop Measuring';
      } else {
        this.fire('measure-map', { stop: true, event });
        this.measureButton.textContent = 'Measure';
      }

      // Track clicking measure button
      logger.info('Clicked measure button');
    });
  },

  _showButton: function () {
    if (this.measureButton) {
      if (!this.measurable && this.measureButton.textContent === 'Measure') this.measureButton.style.display = 'none';
      else this.measureButton.style.display = 'inline-block';
    }
  },

  _initDropOverlay: function () {
    window.DropOverlay = function (map, onReady) {
      this.onReady = onReady;
      this.setMap(map);
    };
    window.DropOverlay.prototype = new google.maps.OverlayView();
    window.DropOverlay.prototype.onAdd = function () {
      this.ready = true;
      if (this.onReady) this.onReady();
    };
    window.DropOverlay.prototype.onRemove = function () {
      /* Nothing to remove */
    };
    window.DropOverlay.prototype.draw = function () {
      /* Nothing to draw */
    };
    window.DropOverlay.prototype.fromPixelToLatLng = function (x, y) {
      var div = this.getMap().getDiv();
      var offset = { x: 0, y: 0 };
      do {
        offset.x += div.offsetLeft;
        offset.y += div.offsetTop;
      } while ((div = div.offsetParent));
      return this.getProjection().fromContainerPixelToLatLng(new google.maps.Point(x - offset.x, y - offset.y));
    };
    window.DropOverlay.prototype.fromLatLngToPixel = function (latLng) {
      var div = this.getMap().getDiv();
      var offset = { x: 0, y: 0 };
      do {
        offset.x += div.offsetLeft;
        offset.y += div.offsetTop;
      } while ((div = div.offsetParent));
      var point = this.getProjection().fromLatLngToContainerPixel(latLng);
      point.x += offset.x;
      point.y += offset.y;
      return point;
    };
  },

  _mapApiLoaded: function () {
    this._initGMap();
  },

  _mapApiError: function (e) {
    var errorMessage = e.detail.value;
    if (errorMessage) {
      this.fire('google-map-api-error', { error: errorMessage });
    }
  },

  _getMapOptions: function () {
    const worldBounds = new google.maps.LatLngBounds(new google.maps.LatLng(-85, -179.999999), new google.maps.LatLng(85, 179.999999));
    var mapOptions = {
      mapId: 'default',
      zoom: this.zoom,
      tilt: this.noAutoTilt ? 0 : 45,
      mapTypeId: this.mapType,
      keyboardShortcuts: false,
      disableDefaultUI: this.disableDefaultUi,
      streetViewControl: !this.disableDefaultUi && !this.disableStreetViewControl,
      streetView: this._getCustomStreetView(),
      disableDoubleClickZoom: this.disableZoom,
      scrollwheel: !this.disableZoom,
      styles: this.styles,
      maxZoom: Number(this.maxZoom),
      minZoom: Number(this.minZoom),
      restriction: {
        latLngBounds: worldBounds,
        strictBounds: true
      },
      cameraControl: false
    };

    // Only override the default if set.
    // We use getAttribute here because the default value of this.draggable = false even when not set.
    if (this.getAttribute('draggable') != null) {
      mapOptions.draggable = this.draggable;
    }
    for (var p in this.additionalMapOptions) mapOptions[p] = this.additionalMapOptions[p];

    return mapOptions;
  },

  _getCustomStreetView: function () {
    var customStreetView = new google.maps.StreetViewPanorama(this.$.map, {
      visible: false,
      enableCloseButton: true,
      addressControlOptions: {
        position: 6
      }
    });
    google.maps.event.addListener(customStreetView, 'position_changed', (e) => {
      this.fire('streetview-position-changed', {
        originalEvent: e,
        position: { lat: customStreetView.getPosition().lat(), lng: customStreetView.getPosition().lng() }
      });
      if (!this.streetViewWindow || this.streetViewWindow.closed) this.fire('streetview-visible-changed', { originalEvent: e });
      customStreetView.setVisible(false);
    });
    google.maps.event.addListener(customStreetView, 'pov_changed', (e) => {
      this.fire('streetview-pov-changed', { originalEvent: e, pov: customStreetView.getPov() });
      customStreetView.setVisible(false);
    });
    return customStreetView;
  },

  _attachChildrenToMap: function (children) {
    if (this.map) {
      for (var i = 0, child; (child = children[i]); ++i) {
        child.map = this.map;
      }
    }
  },

  // watch for future updates to marker objects
  _observeMarkers: function () {
    // Watch for future updates.
    if (this._markersChildrenListener) {
      return;
    }
    this._markersChildrenListener = this.listen(this.$.selector, 'items-changed', '_updateMarkers');
  },

  _updateMarkers: function () {
    var newMarkers = Array.prototype.slice.call(dom(this.$.markers).getDistributedNodes());

    // do not recompute if markers have not been added or removed
    if (newMarkers.length === this.markers.length) {
      var added = newMarkers.filter(
        function (m) {
          return this.markers && this.markers.indexOf(m) === -1;
        }.bind(this)
      );
      if (added.length === 0) {
        // set up observer first time around
        if (!this._markersChildrenListener) {
          this._observeMarkers();
        }
        return;
      }
    }

    this._observeMarkers();

    this.markers = this._setMarkers(newMarkers);

    // Set the map on each marker and zoom viewport to ensure they're in view.
    this._attachChildrenToMap(this.markers);
    if (this.fitToMarkers) {
      this._fitToMarkersChanged();
    }
  },

  // watch for future updates to non-marker objects
  _observeObjects: function () {
    if (this._objectsMutationObserver) {
      return;
    }
    this._objectsMutationObserver = new MutationObserver(this._updateObjects.bind(this));
    this._objectsMutationObserver.observe(this, {
      childList: true
    });
  },

  _updateObjects: function () {
    var newObjects = Array.prototype.slice.call(dom(this.$.objects).getDistributedNodes());

    // Do not recompute if objects have not been added or removed.
    if (newObjects.length === this.objects.length) {
      var added = newObjects.filter(
        function (o) {
          return this.objects.indexOf(o) === -1;
        }.bind(this)
      );
      if (added.length === 0) {
        // Set up observer first time around.
        this._observeObjects();
        return;
      }
    }

    this._observeObjects();
    this._setObjects(newObjects);
    this._attachChildrenToMap(this.objects);
  },

  /**
   * Clears all markers from the map.
   *
   * @method clear
   */
  clear: function () {
    for (var i = 0, m; (m = this.markers[i]); ++i) {
      m.marker.setMap(null);
    }
  },

  /**
   * Explicitly resizes the map, updating its center. This is useful if the
   * map does not show after you have unhidden it.
   *
   * @method resize
   */
  resize: function () {
    if (this.map) {
      // saves and restores latitude/longitude because resize can move the center
      var oldLatitude = this.latitude;
      var oldLongitude = this.longitude;
      google.maps.event.trigger(this.map, 'resize');
      this.latitude = oldLatitude; // restore because resize can move our center
      this.longitude = oldLongitude;

      if (this.fitToMarkers) {
        // we might not have a center if we are doing fit-to-markers
        this._fitToMarkersChanged();
      }
    }
  },

  _loadKml: function () {
    if (this.map && this.kml) {
      var kmlfile = new google.maps.KmlLayer({
        url: this.kml,
        map: this.map
      });
    }
  },

  _debounceUpdateCenter: function () {
    this.debounce('updateCenter', this._updateCenter);
  },

  _updateCenter: function () {
    this.cancelDebouncer('updateCenter');

    if (this.map && this.latitude !== undefined && this.longitude !== undefined) {
      // allow for latitude and longitude to be String-typed, but still Number valued
      var lati = Number(this.latitude);
      if (isNaN(lati)) {
        throw new TypeError('latitude must be a number');
      }
      var longi = Number(this.longitude);
      if (isNaN(longi)) {
        throw new TypeError('longitude must be a number');
      }

      var newCenter = new google.maps.LatLng(lati, longi);
      var oldCenter = this.map.getCenter();

      if (!oldCenter) {
        // If the map does not have a center, set it right away.
        this.map.setCenter(newCenter);
      } else {
        // Using google.maps.LatLng returns corrected lat/lngs.
        oldCenter = new google.maps.LatLng(oldCenter.lat(), oldCenter.lng());

        // If the map currently has a center, slowly pan to the new one.
        if (!oldCenter.equals(newCenter)) {
          this.map.panTo(newCenter);
        }
      }
    }
  },

  _zoomChanged: function () {
    if (this.map) {
      this.map.setZoom(Number(this.zoom));
    }
  },

  _idleEvent: function () {
    if (this.map) {
      this._forwardEvent('idle');
    } else {
      this._clearListener('idle');
    }
  },

  _clickEventsChanged: function () {
    if (this.map) {
      if (this.clickEvents) {
        this._forwardEvent('click');
        this._forwardEvent('dblclick');
        this._forwardEvent('rightclick');
      } else {
        this._clearListener('click');
        this._clearListener('dblclick');
        this._clearListener('rightclick');
      }
    }
  },

  _dragEventsChanged: function () {
    if (this.map) {
      if (this.dragEvents) {
        this._forwardEvent('drag');
        this._forwardEvent('dragend');
        this._forwardEvent('dragstart');
      } else {
        this._clearListener('drag');
        this._clearListener('dragend');
        this._clearListener('dragstart');
      }
    }
  },

  _dragNDropEventsChanged: function () {
    if (this.map && this.dragNDropEvents && this.dropOverlay == null) {
      this.dropOverlay = new DropOverlay(this.map);
      this._listeners['file-dragover'] = this.$.map.addEventListener('dragover', (e) => {
        if (this.dragNDropEvents)
          this.fire('file-drag-over', { originalEvent: e, latLng: this.dropOverlay.fromPixelToLatLng(e.pageX, e.pageY) });
      });
      this._listeners['file-drop'] = this.$.map.addEventListener('drop', (e) => {
        if (this.dragNDropEvents)
          this.fire('file-drop', { originalEvent: e, latLng: this.dropOverlay.fromPixelToLatLng(e.pageX, e.pageY) });
      });
    }
  },

  _mouseEventsChanged: function () {
    if (this.map) {
      if (this.mouseEvents) {
        this._forwardEvent('mousemove');
        this._forwardEvent('mouseout');
        this._forwardEvent('mouseover');
      } else {
        this._clearListener('mousemove');
        this._clearListener('mouseout');
        this._clearListener('mouseover');
      }
    }
  },

  _additionalOptionsChanged: function () {
    if (this.map) {
      this.map.setOptions(this.additionalMapOptions);
    }
  },

  _maxZoomChanged: function () {
    if (this.map) {
      this.map.setOptions({ maxZoom: Number(this.maxZoom) });
    }
  },

  _minZoomChanged: function () {
    if (this.map) {
      this.map.setOptions({ minZoom: Number(this.minZoom) });
    }
  },

  _mapTypeChanged: function (newMapType) {
    if (this.map) {
      // If the custom map type is not available, use roadmap instead
      if (newMapType && newMapType.includes('custom') && !this.map.mapTypes[newMapType]) {
        newMapType = 'roadmap';
      }
    }
    if (this.map) this.map.setMapTypeId(newMapType);
  },

  _disableDefaultUiChanged: function () {
    if (!this.map) {
      return;
    }
    this.map.setOptions({ disableDefaultUI: this.disableDefaultUi });
  },

  _disableMapTypeControlChanged: function () {
    if (!this.map) {
      return;
    }
    this.map.setOptions({ mapTypeControl: !this.disableMapTypeControl });
  },

  _disableStreetViewControlChanged: function () {
    if (!this.map) {
      return;
    }
    this.map.setOptions({ streetViewControl: !this.disableStreetViewControl });
  },

  _disableZoomChanged: function () {
    if (!this.map) {
      return;
    }
    this.map.setOptions({
      disableDoubleClickZoom: this.disableZoom,
      scrollwheel: !this.disableZoom
    });
  },

  attributeChanged: function (attrName) {
    if (!this.map) {
      return;
    }
    // Cannot use *Changed watchers for native properties.
    switch (attrName) {
      case 'draggable':
        this.map.setOptions({ draggable: this.draggable });
        break;
    }
  },

  _fitToMarkersChanged: function () {
    // TODO(ericbidelman): respect user's zoom level.

    if (this.map && this.fitToMarkers && this.markers.length > 0) {
      var latLngBounds = new google.maps.LatLngBounds(),
        count = 0;
      for (var i = 0, m; (m = this.markers[i]); ++i) {
        if (m.noFit) continue;
        ++count;
        latLngBounds.extend(new google.maps.LatLng(m.latitude, m.longitude));
      }

      // For one marker, don't alter zoom, just center it.
      if (count > 1) this.map.fitBounds(latLngBounds);

      if (count > 0) {
        this.map.setCenter(latLngBounds.getCenter());
        this.fire('fit-to-markers');
      }
    }
  },

  _addMapListeners: function () {
    google.maps.event.addListener(this.map, 'center_changed', this.updateSnappedPosition.bind(this));

    google.maps.event.addListener(
      this.map,
      'zoom_changed',
      function () {
        this.zoom = this.map.getZoom();
      }.bind(this)
    );

    google.maps.event.addListener(
      this.map,
      'bounds_changed',
      function () {
        this.fire('map-bounds-changed');
      }.bind(this)
    );

    this._clickEventsChanged();
    this._dragEventsChanged();
    this._dragNDropEventsChanged();
    this._mouseEventsChanged();
    this._idleEvent();
  },

  _addKeyBoardListeners: function () {
    this.keydownHandler = (e) => {
      const target = e.composedPath()[0];
      const isTypingInField = target?.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);
      if (!this.noKeyboardShortcuts && !isTypingInField) {
        switch (e.keyCode) {
          case 107: // numpad add
          case 187: // equals/plus
            this.map.setZoom(this.map.getZoom() + 1);
            break;

          case 109: // numpad subtract
          case 189: // minus
            this.map.setZoom(this.map.getZoom() - 1);
            break;

          case 37: // left
            this.map.panBy(-50, 0);
            break;

          case 38: // up
            this.map.panBy(0, -50);
            break;

          case 39: // right
            this.map.panBy(50, 0);
            break;

          case 40: // down
            this.map.panBy(0, 50);
            break;

          case 33: // page up
            this.map.panBy(0, -500);
            break;

          case 34: // page down
            this.map.panBy(0, 500);
            break;
        }
      }
    };

    // Here we'll track keydowns only when the mouse is over
    // the map container:
    this.$.map.addEventListener('mouseenter', (e) => {
      document.addEventListener('keydown', this.keydownHandler);
    });
    this.$.map.addEventListener('mouseleave', (e) => {
      document.removeEventListener('keydown', this.keydownHandler);
    });
  },

  updateSnappedPosition: function () {
    if (this.map) {
      var c = this.map.getCenter(),
        line = this.snapLine,
        length = this.snapLength,
        angle = this.snapAngle;
      if (line) {
        let snap = KatapultGeometry.SnapToLine(c.lat(), c.lng(), line.p1.latitude, line.p1.longitude, line.p2.latitude, line.p2.longitude);
        if (
          snap &&
          typeof snap.lat === 'number' &&
          typeof snap.long === 'number' &&
          Math.abs(snap.long - c.lng()) > 1e-9 &&
          Math.abs(snap.lat - c.lat()) > 1e-9
        ) {
          this.latitude = snap.lat;
          this.longitude = snap.long;
        }
      } else if (length && angle) {
        this.debounce(
          'snapLengthAndAngle',
          function () {
            // We removed this check 1/17/18 because it seemed unnecessary and thwarted the observer on snapAngle, snapLength and snapLine
            // if (this.justSnappedLengthAndAngle) this.justSnappedLengthAndAngle = false;
            // else {
            let snap = google.maps.geometry.spherical.computeOffset(
              new google.maps.LatLng(length.centerLat, length.centerLon),
              length.length,
              angle.angle
            );
            if (snap) {
              // this.justSnappedLengthAndAngle = true;
              this.latitude = snap.lat();
              this.longitude = snap.lng();
            }
            // }
          },
          200
        );
        this.latitude = c.lat();
        this.longitude = c.lng();
      } else if (length) {
        this.debounce(
          'snapLength',
          function () {
            // if (this.justSnappedLength) this.justSnappedLength = false;
            // else {
            let lengthPoint = new google.maps.LatLng(length.centerLat, length.centerLon);
            let bearing = google.maps.geometry.spherical.computeHeading(lengthPoint, c);
            let snap = google.maps.geometry.spherical.computeOffset(lengthPoint, length.length, bearing);
            if (snap) {
              // this.justSnappedLength = true;
              this.latitude = snap.lat();
              this.longitude = snap.lng();
            }
            // }
          },
          200
        );
      } else if (angle) {
        this.debounce(
          'snapAngle',
          function () {
            // if (this.justSnappedAngle) this.justSnappedAngle = false;
            // else {
            let anglePoint = new google.maps.LatLng(angle.centerLat, angle.centerLon);
            let distance = google.maps.geometry.spherical.computeDistanceBetween(anglePoint, c);
            let snap = google.maps.geometry.spherical.computeOffset(anglePoint, distance, angle.angle);
            if (snap) {
              // this.justSnappedAngle = true;
              this.latitude = snap.lat();
              this.longitude = snap.lng();
            }
            // }
          },
          200
        );
      } else {
        this.latitude = c.lat();
        this.longitude = c.lng();
      }
    }
  },

  _clearListener: function (name) {
    if (this._listeners[name]) {
      google.maps.event.removeListener(this._listeners[name]);
      this._listeners[name] = null;
    }
  },

  _forwardEvent: function (name) {
    this._listeners[name] = google.maps.event.addListener(
      this.map,
      name,
      function (event) {
        this.fire('google-map-' + name, event);
      }.bind(this)
    );
  },

  _deselectMarker: function (e, detail) {
    // If singleInfoWindow is set, update iron-selector's selected attribute to be null.
    // Else remove the marker from iron-selector's selected array.
    var markerIndex = this.$.selector.indexOf(e.target);

    if (this.singleInfoWindow) {
      this.$.selector.selected = null;
    } else if (this.$.selector.selectedValues) {
      this.$.selector.selectedValues = this.$.selector.selectedValues.filter(function (i) {
        return i !== markerIndex;
      });
    }
  }
});
